import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  DeleteVideoPrivateCommentResult,
  DeleteVideoPrivateCommentOptions,
  PatchVideoPrivateCommentResult,
  PatchVideoPrivateCommentOptions,
  DeleteVideoPrivateCommentFields,
  PatchVideoPrivateCommentFields,
  deleteVideoPrivateComment,
  patchVideoPrivateComment,
} from '../fetchers/video/private_comment';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseDeleteVideoPrivateCommentOptions<Fields extends DeleteVideoPrivateCommentFields> =
  DeleteVideoPrivateCommentOptions<Fields>;

/**
 * Delete a private comment
 * This method deletes the specified private comment. The authenticated user must be the owner of either the comment or the video.
 */

export function useDeleteVideoPrivateComment<Fields extends DeleteVideoPrivateCommentFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<DeleteVideoPrivateCommentResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UseDeleteVideoPrivateCommentOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/private_comments/${
            options.where.commentId
          }${serializeQuery(options)}`,
          deleteVideoPrivateComment({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteVideoPrivateComment, {
    endpoint: '/videos/:videoId/private_comments/:commentId',
    method: 'DELETE',
  });
}

type UsePatchVideoPrivateCommentOptions<Fields extends PatchVideoPrivateCommentFields> =
  PatchVideoPrivateCommentOptions<Fields>;

/**
 * Edit a private comment
 * This method edits the specified private comment. The authenticated user must be the owner of the comment.
 */

export function usePatchVideoPrivateComment<Fields extends PatchVideoPrivateCommentFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PatchVideoPrivateCommentResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePatchVideoPrivateCommentOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/private_comments/${
            options.where.commentId
          }${serializeQuery(options)}`,
          patchVideoPrivateComment({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePatchVideoPrivateComment, {
    endpoint: '/videos/:videoId/private_comments/:commentId',
    method: 'PATCH',
  });
}
